import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { getVesselIncineratorEmissionAnalysis } from "../../../../services/vesselMachineryService";
import { generateDateTicks } from "../../../../services/dataTicks";
import { SlidersHorizontal } from "lucide-react";

const EmissionAnalysisChart = () => {
  const { startDate, endDate } = useSelector((state) => state.dateRange);
  const { currentVessel } = useSelector((state) => state.vesselOverview);
  const { selectedVoyage } = useSelector((state) => state.voyageFilter); // Add voyage filter

  const [emissionData, setEmissionData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedParams, setSelectedParams] = useState([
    "CO2",
    "N2O",
    "CH4",
    "GHG",
  ]);

  // Define color codes for each emission parameter
  const emissionColors = {
    CO2: "#566573", // Gray for CO₂
    N2O: "#d35400", // Orange for N₂O
    CH4: "#a93226", // Red for CH₄
    GHG: "#7d3c98", // Purple for GHG (total)
  };

  // Emission parameter labels
  const emissionLabels = {
    CO2: "CO₂",
    N2O: "N₂O",
    CH4: "CH₄",
    GHG: "GHG Intensity",
  };

  // Fetch emission data from API
  useEffect(() => {
    const fetchEmissionData = async () => {
      // Skip if no vessel selected
      if (!currentVessel?._id) {
        setEmissionData(null);
        return;
      }

      // Skip if no voyage selected AND no date range
      if (!selectedVoyage && (!startDate || !endDate)) {
        setEmissionData(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getVesselIncineratorEmissionAnalysis(
          currentVessel._id,
          selectedVoyage ? null : startDate, // Pass null for dates if voyage selected
          selectedVoyage ? null : endDate, // Pass null for dates if voyage selected
          selectedVoyage?._id || null // Pass voyage ID if selected
        );

        if (response?.status === "success") {
          setEmissionData(response.data);
        } else {
          setError(
            response?.message || "Failed to fetch incinerator emission data"
          );
          setEmissionData(null);
        }
      } catch (err) {
        console.error("Error fetching incinerator emission data:", err);
        setError(err.message);
        setEmissionData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchEmissionData();
  }, [currentVessel, startDate, endDate, selectedVoyage]); // Add selectedVoyage to dependencies

  // Transform API data to Highcharts format
  const transformDataForChart = () => {
    if (!emissionData?.emissions || emissionData.emissions.length === 0) {
      return {
        categories: [],
        series: [],
      };
    }

    const categories = emissionData.emissions.map((item) => {
      const date = new Date(item.date);
      return date.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
      });
    });

    const seriesData = {
      CO2: [],
      N2O: [],
      CH4: [],
      GHG: [],
    };

    emissionData.emissions.forEach((item, index) => {
      const date = new Date(item.date);
      const fullDate = date.toLocaleDateString("en-US", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      // Convert kg to g for chart display (MT/day as per your original chart)
      const co2Value = item.emissions.co2.value;
      const n2oValue = item.emissions.n2o.value;
      const ch4Value = item.emissions.ch4.value;
      const ghgValue = item.emissions.ghg.value; 

      seriesData.CO2.push({
        x: new Date(item.date).getTime(),
        y: co2Value,
        fullDate: fullDate,
        color: emissionColors.CO2,
      });

      seriesData.N2O.push({
        x: new Date(item.date).getTime(),
        y: n2oValue,
        fullDate: fullDate,
        color: emissionColors.N2O,
      });

      seriesData.CH4.push({
        x: new Date(item.date).getTime(),
        y: ch4Value,
        fullDate: fullDate,
        color: emissionColors.CH4,
      });

      seriesData.GHG.push({
        x: new Date(item.date).getTime(),
        y: ghgValue,
        fullDate: fullDate,
        color: emissionColors.GHG,
      });
    });

    // return {
    //   categories,
    //   series: selectedParams.map((param) => ({
    //     name: emissionLabels[param],
    //     data: seriesData[param],
    //     color: emissionColors[param],
    //     marker: {
    //       enabled: true,
    //       radius: 4,
    //       fillColor: emissionColors[param],
    //       lineWidth: 1,
    //     },
    //   })),
    // };
    return {
      categories,
      series: selectedParams.map(param => ({
        name: emissionLabels[param],
        data: seriesData[param],
        color: emissionColors[param],
        yAxis: (param === "GHG" ? 1 : 0), // <-- THIS IS THE FIX
        marker: {
          enabled: true,
          radius: 4,
          fillColor: emissionColors[param],
          lineColor: "#fff",
          lineWidth: 1,
        },
      }))
    };
  };

  const chartData = transformDataForChart();

  const dates = emissionData?.emissions?.map((item) => item.date) || [];
  const tickPositions = generateDateTicks(dates);

  const options = {
    chart: {
      type: "line",
      height: "500",
      backgroundColor: "transparent",
    },
    title: {
      text: null,
    },
    xAxis: {
      type: "datetime",
      tickPositions: tickPositions,
      title: {
        text: "Dates",
        style: {
          color: "#718096",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      labels: {
        style: {
          color: "#4a5568",
          fontSize: "11px",
        },
        rotation: -45,
      },
    },
    // yAxis: {
    //   startOnTick: false,
    //   endOnTick: false,
    //   title: {
    //     text: "Emissions (MT/day and g CO₂e/MJ)",
    //     style: {
    //       color: "#718096",
    //       fontSize: "12px",
    //       fontWeight: "500",
    //     },
    //   },
    //   labels: {
    //     style: {
    //       color: "#718096",
    //     },
    //   },
    //   minPadding: 0,
    //   maxPadding: 0.05,
    //   tickAmount: 6,
    // },
    yAxis: [
      {

        title: {
          text: "Emissions (MT/day)",
          style: {
            color: "#718096",
            fontSize: "12px",
            fontWeight: "500",
          },
        },
        labels: {
          style: {
            color: "#718096",
          },
          formatter: function () {
            return this.value;
          },
        },
        min: 0,
        startOnTick: true,
        minPadding: 0,
        maxPadding: 0.05,
        tickAmount: 6,
      },
      {
        
        title: {
          text: "GHG Intensity (g CO₂e/MJ)",
          style: {
            color: "#7d3c98",
            fontSize: "12px",
            fontWeight: "500",
          },
        },
        labels: {
          style: {
            color: "#7d3c98",
          },
          formatter: function () {
            return this.value;
          },
        },
        min: 0,
        startOnTick: true,
        minPadding: 0,
        maxPadding: 0.05,
        tickAmount: 6,
        opposite: true, 
      },
    ],
    // tooltip: {
    //   useHTML: true,
    //   headerFormat: "<table>",
    //   pointFormat:
    //     '<tr><th colspan="2"><h3>{point.fullDate}</h3></th></tr>' +
    //     '<tr><th>Emission Type:</th><td style="color:{point.color}"><b>{series.name}</b></td></tr>' +
    //     "<tr><th>Value:</th><td><b>{point.y:.2f} MT/day</b></td></tr>",
    //   footerFormat: "</table>",
    //   backgroundColor: "rgba(255, 255, 255, 0.98)",
    //   borderColor: "#e2e8f0",
    //   borderRadius: 8,
    //   shadow: true,
    //   style: {
    //     fontSize: "12px",
    //   },
    // },
    tooltip: {
      useHTML: true,
      shared: true,
      crosshairs: true,
      headerFormat:
        '<div style="font-weight:600;font-size:12px;margin-bottom:5px">{point.x:%b %e, %Y}</div>',
      formatter: function () {
        return this.points
          .map((point) => {
            const unit =
              point.series.name === "GHG Intensity" ? "g CO₂e/MJ" : "MT/day";
            return `
              <div style="display:flex;justify-content:space-between">
                <span style="color:${point.series.color}">${point.series.name}:</span>
                <span style="font-weight:700;margin-left:10px">${point.y} ${unit}</span>
              </div>
            `;
          })
          .join("");
      },
      backgroundColor: "#ffffff",
      borderColor: "#e5e7eb",
      borderRadius: 6,
      shadow: true,
      style: {
        fontSize: "12px",
      },
    },
    plotOptions: {
      line: {
        marker: {
          enabled: true,
          radius: 4,
        },
      },
    },
    series: chartData.series,
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: "#566573",
        fontSize: "11px",
        fontWeight: "500",
      },
    },
    credits: {
      enabled: false,
    },
    responsive: {
      rules: [
        {
          condition: {
            maxWidth: 500,
          },
          chartOptions: {
            legend: {
              layout: "horizontal",
              align: "center",
              verticalAlign: "bottom",
            },
            xAxis: {
              labels: {
                rotation: -30,
              },
            },
          },
        },
      ],
    },
  };

  const toggleEmissionParam = (param) => {
    setSelectedParams((prev) =>
      prev.includes(param) ? prev.filter((p) => p !== param) : [...prev, param]
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#566573]"></div>
            <p className="mt-3 text-gray-600 font-medium">
              Loading incinerator emission data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center max-w-md">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-red-50 rounded-full mb-3">
              <svg
                className="w-6 h-6 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Unable to load data
            </h3>
            <p className="text-gray-600 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // No data selected state
  if (!emissionData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No vessel or filter selected</div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Emission Parameter Selector */}
      <div className="flex items-center gap-4 mb-4">
        <label className="text-base font-semibold text-gray-700 flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-[#27ade3]" />
          Select by:
        </label>
        <div className="flex flex-wrap gap-3">
          {Object.entries(emissionLabels).map(([key, label]) => (
            <button
              key={key}
              onClick={() => toggleEmissionParam(key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${selectedParams.includes(key)
                  ? "text-white shadow-sm"
                  : "text-gray-600 bg-gray-100 hover:bg-gray-200"
                }`}
              style={{
                backgroundColor: selectedParams.includes(key)
                  ? emissionColors[key]
                  : "",
                border: selectedParams.includes(key)
                  ? `1px solid ${emissionColors[key]}`
                  : "1px solid #e2e8f0",
              }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart */}
      {emissionData &&
        emissionData.emissions &&
        emissionData.emissions.length > 0 ? (
        <HighchartsReact highcharts={Highcharts} options={options} />
      ) : (
        <div className="flex items-center justify-center h-64 border-2 border-dashed border-gray-300 rounded-lg">
          <div className="text-gray-500">No chart data available</div>
        </div>
      )}
    </div>
  );
};

export default EmissionAnalysisChart;
